import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../AuthContext";
import s from "../../admin_styles/AdminDashboard.module.css";

export default function ExternalApiKeysManager() {
  const { role } = useContext(AuthContext);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [formData, setFormData] = useState({
    issued_to: "",
    purpose: "",
    note: "",
    permissions: {
      scopes: ["mail.send"],
      channels: {
        mail: {
          max_recipients_per_request: 200,
          max_requests_per_minute: 30,
        },
      },
    },
  });

  // Загрузка списка ключей
  const loadKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/admin/external_api_keys", {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Ошибка загрузки ключей");
      const data = await response.json();
      setKeys(data.keys || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role === "superadmin") {
      loadKeys();
    }
  }, [role]);

  // Создание нового ключа
  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/admin/external_api_keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Ошибка создания ключа");

      const data = await response.json();
      setSuccess(`Ключ создан успешно! ID: ${data.id}`);
      setFormData({
        issued_to: "",
        purpose: "",
        note: "",
        permissions: {
          scopes: ["mail.send"],
          channels: {
            mail: {
              max_recipients_per_request: 200,
              max_requests_per_minute: 30,
            },
          },
        },
      });
      setShowForm(false);
      loadKeys();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Удаление ключа
  const handleDelete = async (keyId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/admin/external_api_keys", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: keyId }),
      });

      if (!response.ok) throw new Error("Ошибка удаления ключа");

      setSuccess("Ключ удален успешно");
      setDeleteConfirm(null);
      loadKeys();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Возвращаем полный ключ без маскирования
  const displayKey = (key) => key;

  if (role !== "superadmin") {
    return (
      <div style={{ padding: "20px", color: "red" }}>
        Доступ запрещен. Только суперадмину.
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2>Управление внешними API ключами</h2>

      {error && (
        <div style={{ color: "red", marginBottom: "10px", padding: "10px" }}>
          Ошибка: {error}
        </div>
      )}

      {success && (
        <div
          style={{ color: "green", marginBottom: "10px", padding: "10px" }}
        >
          {success}
        </div>
      )}

      <button
        onClick={() => setShowForm(!showForm)}
        style={{
          marginBottom: "20px",
          padding: "10px 20px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        {showForm ? "Скрыть форму" : "Создать новый ключ"}
      </button>

      {showForm && (
        <form
          onSubmit={handleCreate}
          style={{
            marginBottom: "20px",
            padding: "15px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            backgroundColor: "#f9f9f9",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            <label>
              Кому выдан (issued_to): *
              <input
                type="text"
                required
                value={formData.issued_to}
                onChange={(e) =>
                  setFormData({ ...formData, issued_to: e.target.value })
                }
                style={{
                  display: "block",
                  width: "100%",
                  padding: "8px",
                  marginTop: "5px",
                  boxSizing: "border-box",
                }}
              />
            </label>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label>
              Назначение (purpose): *
              <input
                type="text"
                required
                value={formData.purpose}
                onChange={(e) =>
                  setFormData({ ...formData, purpose: e.target.value })
                }
                style={{
                  display: "block",
                  width: "100%",
                  padding: "8px",
                  marginTop: "5px",
                  boxSizing: "border-box",
                }}
              />
            </label>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label>
              Примечание:
              <textarea
                value={formData.note}
                onChange={(e) =>
                  setFormData({ ...formData, note: e.target.value })
                }
                style={{
                  display: "block",
                  width: "100%",
                  padding: "8px",
                  marginTop: "5px",
                  boxSizing: "border-box",
                  minHeight: "60px",
                }}
              />
            </label>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label>
              Разрешения (JSON):
              <textarea
                value={JSON.stringify(formData.permissions, null, 2)}
                onChange={(e) => {
                  try {
                    setFormData({
                      ...formData,
                      permissions: JSON.parse(e.target.value),
                    });
                  } catch (err) {
                    // Игнорируем ошибки парсинга во время редактирования
                  }
                }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "8px",
                  marginTop: "5px",
                  boxSizing: "border-box",
                  minHeight: "120px",
                  fontFamily: "monospace",
                }}
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: "10px 20px",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Создание..." : "Создать ключ"}
          </button>
        </form>
      )}

      <h3>Существующие ключи ({keys.length})</h3>

      {loading && keys.length === 0 ? (
        <p>Загрузка...</p>
      ) : keys.length === 0 ? (
        <p>Нет созданных ключей</p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f5f5f5" }}>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                ID
              </th>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Ключ
              </th>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Кому выдан
              </th>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Назначение
              </th>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Активен
              </th>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Последнее использование
              </th>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Действия
              </th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key.id} style={{ borderBottom: "1px solid #ddd" }}>
                <td style={{ padding: "10px" }}>{key.id}</td>
                <td
                  style={{
                    padding: "10px",
                    fontFamily: "monospace",
                    fontSize: "12px",
                  }}
                >
                  {displayKey(key.raw_key)}
                </td>
                <td style={{ padding: "10px" }}>{key.issued_to}</td>
                <td style={{ padding: "10px" }}>{key.purpose}</td>
                <td style={{ padding: "10px" }}>
                  {key.is_active ? (
                    <span style={{ color: "green" }}>✓ Активен</span>
                  ) : (
                    <span style={{ color: "red" }}>✗ Неактивен</span>
                  )}
                </td>
                <td style={{ padding: "10px", fontSize: "12px" }}>
                  {key.last_used_at
                    ? new Date(key.last_used_at).toLocaleString("ru-RU")
                    : "Никогда"}
                </td>
                <td style={{ padding: "10px" }}>
                  <button
                    onClick={() => setDeleteConfirm(key.id)}
                    disabled={loading}
                    style={{
                      padding: "5px 10px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.7 : 1,
                    }}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal - Подтверждение удаления */}
      {deleteConfirm !== null && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "30px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              maxWidth: "400px",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "20px" }}>
              Вы уверены?
            </h3>
            <p style={{ marginBottom: "30px", color: "#666" }}>
              Ключ будет удален безвозвратно. Это действие нельзя отменить.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={loading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? "Удаление..." : "Удалить"}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={loading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
